import { useMemo, useState, type FormEvent } from 'react';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconDownload } from '../components/icons';
import { Modal } from '../components/Modal';
import Pagination from '../components/Pagination';
import { useAuth } from '../auth/useAuth';
import { useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { downloadFile } from '../utils/download';
import { formatMoney } from '../utils/format';
import usePaginatedList from '../hooks/usePaginatedList';

interface ChequeRow {
  id: number;
  cheque_number: string;
  benefit_claim: { id: number; reference: string; member: { user: { first_name: string; last_name: string } | null } } | null;
  payment_voucher: { id: number; pv_number: string } | null;
  payee_name: string;
  amount: string;
  status: string;
  required_signatures: number;
  signatures_collected: number;
}

const chequeReference = (cheque: ChequeRow) => cheque.benefit_claim?.reference ?? cheque.payment_voucher?.pv_number ?? '—';

const STATUS_LABELS: Record<string, string> = {
  pending_signature: 'Pending Signature',
  signed: 'Signed',
  issued: 'Issued',
  voided: 'Voided',
  spoiled: 'Spoiled',
};

const STATUS_BADGE: Record<string, string> = {
  pending_signature: 'badge-scheduled',
  signed: 'badge-active',
  issued: 'badge-active',
  voided: 'badge-urgent',
  spoiled: 'badge-urgent',
};

export function Cheques() {
  const { can } = useAuth();
  const { settings } = useConfig();
  const confirmAction = useConfirm();
  const { showError, showSuccess } = useToast();
  const [status, setStatus] = useState('');

  const [signing, setSigning] = useState<ChequeRow | null>(null);
  const [signForm, setSignForm] = useState({ current_password: '', two_factor_code: '', reference: '' });
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const [voiding, setVoiding] = useState<ChequeRow | null>(null);
  const [voidReason, setVoidReason] = useState('');

  const canSign = can('memberships.benefits.cheque.sign');
  const canDisburse = can('memberships.benefits.disburse');

  const filters = useMemo(() => ({ status: status || undefined }), [status]);
  const { data, loading, error, setPage, reload } = usePaginatedList<ChequeRow>('/cheques', filters);

  const fmtMoney = (n: string) => formatMoney(Number(n), settings.currency);

  const openSign = (cheque: ChequeRow) => {
    setSignForm({ current_password: '', two_factor_code: '', reference: '' });
    setErrors({});
    setSigning(cheque);
  };

  const submitSign = async (e: FormEvent) => {
    e.preventDefault();
    if (!signing) return;
    try {
      await api.post(`/cheques/${signing.id}/sign`, signForm);
      setSigning(null);
      showSuccess('Cheque signed.');
      reload();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] });
    }
  };

  const markIssued = async (cheque: ChequeRow) => {
    if (!(await confirmAction(`Mark cheque ${cheque.cheque_number} as issued/handed over?`, { confirmLabel: 'Mark Issued' }))) return;
    try {
      await api.post(`/cheques/${cheque.id}/mark-issued`);
      reload();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Failed.');
    }
  };

  const submitVoid = async (e: FormEvent) => {
    e.preventDefault();
    if (!voiding) return;
    try {
      await api.post(`/cheques/${voiding.id}/void`, { void_reason: voidReason });
      setVoiding(null);
      setVoidReason('');
      reload();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Failed.');
    }
  };

  const downloadPdf = (cheque: ChequeRow) => downloadFile(api, `/cheques/${cheque.id}/pdf`, `${cheque.cheque_number}.pdf`);

  return (
    <>
      <header className="page-header">
        <h1>Cheque Register</h1>
      </header>

      <div className="toolbar">
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Cheque Number</th>
                <th>Reference</th>
                <th>Payee</th>
                <th>Amount</th>
                <th>Signatures</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={7} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((cheque) => (
                  <tr key={cheque.id}>
                    <td className="muted">{cheque.cheque_number}</td>
                    <td>{chequeReference(cheque)}</td>
                    <td>{cheque.payee_name}</td>
                    <td>{fmtMoney(cheque.amount)}</td>
                    <td>{cheque.signatures_collected}/{cheque.required_signatures}</td>
                    <td>
                      <span className={`badge ${STATUS_BADGE[cheque.status] ?? 'badge-neutral'}`}>
                        {STATUS_LABELS[cheque.status] ?? cheque.status}
                      </span>
                    </td>
                    <td className="right">
                      <IconButton icon={IconDownload} label="Download PDF" onClick={() => downloadPdf(cheque)} />
                      {canSign && cheque.status === 'pending_signature' && (
                        <button type="button" className="btn btn-ghost" onClick={() => openSign(cheque)}>
                          Sign
                        </button>
                      )}
                      {canDisburse && cheque.status === 'signed' && (
                        <button type="button" className="btn btn-ghost" onClick={() => markIssued(cheque)}>
                          Mark Issued
                        </button>
                      )}
                      {canDisburse && !['voided', 'issued'].includes(cheque.status) && (
                        <button
                          type="button"
                          className="btn btn-danger-ghost"
                          onClick={() => {
                            setVoidReason('');
                            setVoiding(cheque);
                          }}
                        >
                          Void
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="muted center">
                    No cheques yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>

      {signing && (
        <Modal title={`Sign cheque ${signing.cheque_number}`} onClose={() => setSigning(null)}>
          <form onSubmit={submitSign} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <p className="muted span-2">
              This re-authenticates you as the individually issued signing credential for this cheque — not a shared
              login. Enter your own account password to confirm.
            </p>
            <label className="span-2">
              Current password *
              <input
                type="password"
                value={signForm.current_password}
                onChange={(e) => setSignForm({ ...signForm, current_password: e.target.value })}
                required
              />
              {errors.current_password && <small className="error">{errors.current_password[0]}</small>}
            </label>
            <label className="span-2">
              Two-factor code (if enabled on your account)
              <input
                value={signForm.two_factor_code}
                onChange={(e) => setSignForm({ ...signForm, two_factor_code: e.target.value })}
              />
              {errors.two_factor_code && <small className="error">{errors.two_factor_code[0]}</small>}
            </label>
            <label className="span-2">
              Type the reference to confirm ({chequeReference(signing)}) *
              <input
                value={signForm.reference}
                onChange={(e) => setSignForm({ ...signForm, reference: e.target.value })}
                required
              />
              {errors.reference && <small className="error">{errors.reference[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setSigning(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Sign</button>
            </div>
          </form>
        </Modal>
      )}

      {voiding && (
        <Modal title={`Void cheque ${voiding.cheque_number}`} onClose={() => setVoiding(null)}>
          <form onSubmit={submitVoid} className="form-grid">
            <label className="span-2">
              Reason *
              <textarea rows={3} value={voidReason} onChange={(e) => setVoidReason(e.target.value)} required />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setVoiding(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Void</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
